import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { alpha, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import debounce from "lodash.debounce";
import { AnimatePresence, motion } from "framer-motion";

// Material UI
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import Paper from "@mui/material/Paper";
import MapIcon from "@mui/icons-material/Map";
import ViewListIcon from "@mui/icons-material/ViewList";

// Logic & API
import { validateArrayIncludes } from "../../common/validators";
import TokenService from "../../store/services/token.service";
import { connectSocket, getSocket } from "../../api/socketService";

// Redux actions
import {
  searchForOrder,
  setShowDispatchOrdersModal,
  updatedOrders,
  updateOrders,
  updateOrderStatuses,
  updateOrdersToDispatch,
  updateStatusOrders,
} from "../../store/actions/ordersSlice";
import {
  setAlignment,
  setMapViewState,
} from "../../store/actions/dashboardSlice";
import { fetchShippingRoutes } from "../../store/actions/shippingRoutesSlice";

// Components
import OrdersMap from "../map/ordersMap";
import FloatingMenu from "./FloatingMenu";
import DashboardTab from "./DashboardTab";
import DashboardOrderList from "./DashboardOrderList";
import DashboardStatusOrderList from "./DashboardStatusOrderList";
import DashboardShippingRoutes from "./DashboardShippingRoutes";
import DashboardSmartRoutes from "./DashboardSmartRoutes";
import Packing from "./Packing";
import EnhancedSaloon from "./Tables/EnhancedSaloon";

// New components
import FloatingMapPreview from "./FloatingMapPreview";
import OrderGrid from "./OrderGrid";
import SearchBar from "./SearchBar";
import DispatchControls from "./DispatchControls";
import InstructionsPanel from "./InstructionsPanel";
import OrderLimitModal from "../subscription/OrderLimitModal";
import { getSubscriptionUsage } from "../../api/subscription";
import { setMapOrderLimit } from "../../store/actions/mapSlice";

// Hooks
import useWakeLock from "../../hooks/useWakeLock";
import useDashboardData from "../../hooks/useDashboardData";
import useOrderOperations from "../../hooks/useOrderOperations";

export default function Dashboard({ mode = "orders" }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  // Selectors
  const alignment = useSelector((state) => state.dashboard.alignment);
  const ordersToDispatch = useSelector(
    (state) => state.orders.ordersToDispatch
  );
  const orders = useSelector((state) => state.orders.orders);
  const deliveryCenter = useSelector(
    (state) => state.deliveryCenter.deliveryCenter
  );
  const mapViewState = useSelector((state) => state.dashboard.mapViewState);
  const orderLimitOpen = useSelector(
    (state) => state.orders.orderLimitModal?.open
  );

  // State
  const fromLogin = location.state?.fromLogin === true;
  const [orderUsage, setOrderUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [splashActive, setSplashActive] = useState(true);
  const [hasSocket, setHasSocket] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMapOnMobile, setShowMapOnMobile] = useState(false);
  const [showFloatingMap, setShowFloatingMap] = useState(false);
  const [viewMode, setViewMode] = useState("list");

  // Refs
  const orderStatusInterval = useRef(null);
  const heartbeatInterval = useRef(null);
  const mapPreviewTimeoutRef = useRef(null);
  const prevDispatchCount = useRef(0);

  // Order-dispatch quota usage (for the proactive "approaching limit" warning)
  // and the map's display cap (max_map_orders), which the map selector applies.
  const fetchOrderUsage = useCallback(async () => {
    try {
      const { data } = await getSubscriptionUsage();
      setOrderUsage(data?.usage?.order || null);
      dispatch(
        setMapOrderLimit(data?.display_limits?.map_orders?.limit ?? -1)
      );
    } catch (e) {
      // Usage is a non-critical hint; ignore failures.
    }
  }, [dispatch]);

  // Fetch on mount and refresh whenever the limit is hit (keeps the bar in sync).
  useEffect(() => {
    fetchOrderUsage();
  }, [fetchOrderUsage, orderLimitOpen]);

  // Refresh usage after a dispatch clears the selection (count returns to 0),
  // so the remaining-orders warning reflects the just-consumed quota.
  useEffect(() => {
    const count = ordersToDispatch.length;
    if (prevDispatchCount.current > 0 && count === 0) {
      fetchOrderUsage();
    }
    prevDispatchCount.current = count;
  }, [ordersToDispatch.length, fetchOrderUsage]);

  // Sync alignment from route mode
  useEffect(() => {
    if (mode && mode !== alignment) {
      dispatch(setAlignment(mode));
    }
  }, [mode]);

  // Custom hooks
  useWakeLock();
  useDashboardData();
  const {
    handleOrderDetails,
    handleAcceptOrder,
    handlePrintOrder,
    getOrdersArray,
  } = useOrderOperations();

  // Logic Handlers (Unchanged)
  const handleViewModeChange = useCallback((newViewMode) => {
    setViewMode(newViewMode);
  }, []);

  const handleShowMapPreview = useCallback(() => {
    if (mapViewState === "hidden") {
      if (mapPreviewTimeoutRef.current)
        clearTimeout(mapPreviewTimeoutRef.current);
      mapPreviewTimeoutRef.current = setTimeout(
        () => setShowFloatingMap(true),
        500
      );
    }
  }, [mapViewState]);

  const handleHideMapPreview = useCallback(() => {
    if (mapPreviewTimeoutRef.current)
      clearTimeout(mapPreviewTimeoutRef.current);
    setShowFloatingMap(false);
  }, []);

  const startHeartbeat = () => {
    if (heartbeatInterval.current) clearInterval(heartbeatInterval.current);
    heartbeatInterval.current = setInterval(() => {
      dispatch(updatedOrders());
      checkSocketConnection();
    }, 30000);
  };

  const startOrderStatusInterval = () => {
    if (orderStatusInterval.current) clearInterval(orderStatusInterval.current);
    orderStatusInterval.current = setInterval(() => {
      dispatch(updateOrderStatuses());
    }, 30000);
  };

  const checkSocketConnection = () => {
    const socket = getSocket();
    if (!socket || socket.disconnected) {
      const token = TokenService.getLocalAccessToken();
      const newSocket = connectSocket(token);
      setHasSocket(!!newSocket);
    }
  };

  const initializeSocket = () => {
    const token = TokenService.getLocalAccessToken();
    const socket = connectSocket(token);
    setHasSocket(!!socket);
  };

  useEffect(() => {
    startHeartbeat();
    startOrderStatusInterval();
    initializeSocket();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        dispatch(
          updateOrders({ newSearch: true, includeNeighborhoodGroups: true })
        );
        initializeSocket();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      if (heartbeatInterval.current) clearInterval(heartbeatInterval.current);
      if (orderStatusInterval.current)
        clearInterval(orderStatusInterval.current);
      const socket = getSocket();
      if (socket) socket.close();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    setLoading(!deliveryCenter);
  }, [deliveryCenter]);

  useEffect(() => {
    const duration = fromLogin ? 3000 : 1000;
    const timer = setTimeout(() => {
      setSplashActive(false);
    }, duration);
    if (fromLogin) {
      // Clear the flag so browser back/forward won't re-trigger
      history.replace(location.pathname, {});
    }
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    return () => {
      if (mapPreviewTimeoutRef.current)
        clearTimeout(mapPreviewTimeoutRef.current);
    };
  }, []);

  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query === "") {
        switch (alignment) {
          case "orders":
            return dispatch(updateOrders({ newSearch: true }));
case "drivers-delivering":
            return dispatch(fetchShippingRoutes({ query }));
          default:
            return dispatch(updateOrders({ newSearch: true }));
        }
      } else {
        switch (alignment) {
          case "drivers-delivering":
            return dispatch(fetchShippingRoutes({ query }));
          default:
            return dispatch(searchForOrder({ query, alignment }));
        }
      }
    }, 300),
    [dispatch, alignment]
  );

  const handleOrderSearch = (e) => {
    const query = e.target.value;
    debouncedSearch(query);
  };

  const handleClear = () => {
    setSearchQuery("");
    debouncedSearch("");
  };

  const handleAlignment = (event, newAlignment) => {
    dispatch(setAlignment(newAlignment));
  };

  const showDispatchOrdersModal = (withProviders = false) => {
    dispatch(setShowDispatchOrdersModal({ show: true, withProviders }));
  };

  const clearOrdersToDispatch = () => {
    dispatch(updateOrdersToDispatch([]));
  };

  const handleMapViewStateChange = () => {
    const nextState = mapViewState === "hidden" ? "map" : "hidden";
    dispatch(setMapViewState(nextState));
  };

  const toggleMobileMapView = () => {
    setShowMapOnMobile(!showMapOnMobile);
  };

  // UI Helper Functions
  const switchAlignmentComponents = () => {
    const isMapHidden = mapViewState === "hidden";
    const ordersArray = getOrdersArray(orders);

    // Common props
    const gridProps = {
      orders: ordersArray,
      onViewDetails: handleOrderDetails,
      onAcceptOrder: handleAcceptOrder,
      onPrintOrder: handlePrintOrder,
    };

    const listProps = { viewMode };

    switch (alignment) {
      case "orders":
        return isMapHidden ? (
          <OrderGrid {...gridProps} />
        ) : (
          <DashboardOrderList {...listProps} />
        );
case "drivers-delivering":
        return <DashboardShippingRoutes isSidebar={isMapVisible} />;
      case "smart-routes":
        return <DashboardSmartRoutes />;
      case "orders-completed":
        return isMapHidden ? (
          <OrderGrid {...gridProps} />
        ) : (
          <DashboardStatusOrderList status="orders-completed" {...listProps} />
        );
      case "saloon":
        return <EnhancedSaloon />;
      case "packing":
        return <Packing />;
      default:
        return isMapHidden ? (
          <OrderGrid {...gridProps} />
        ) : (
          <DashboardOrderList {...listProps} />
        );
    }
  };

  const isFullPageMode = validateArrayIncludes(
    ["packing", "saloon"],
    alignment
  );
  const shouldShowMobileToggle = !isFullPageMode;
  const isMapVisible = !isFullPageMode && mapViewState === "map";

  if (loading || !deliveryCenter || splashActive) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `radial-gradient(circle at 50% 40%, ${alpha(
            theme.palette.primary.light,
            0.08
          )} 0%, ${theme.palette.background.default} 70%)`,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
        >
          <motion.div
            animate={{
              scale: [1, 1.06, 1],
              boxShadow: [
                `0 0 0 0px ${alpha(theme.palette.primary.main, 0.15)}`,
                `0 0 0 18px ${alpha(theme.palette.primary.main, 0)}`,
                `0 0 0 0px ${alpha(theme.palette.primary.main, 0.15)}`,
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{ borderRadius: "50%" }}
          >
            <Box
              component="img"
              src="/images/logo.png"
              alt="Slapie"
              sx={{
                width: { xs: 80, md: 96 },
                height: { xs: 80, md: 96 },
                borderRadius: "50%",
                objectFit: "cover",
                border: `3px solid ${theme.palette.divider}`,
              }}
            />
          </motion.div>
        </motion.div>
      </Box>
    );
  }

  // Animation Variants
  const sidebarVariants = {
    mapVisible: {
      width: isMobile ? "100%" : isTablet ? "50%" : "40%", // Responsive width
      x: 0,
      opacity: 1,
      borderRadius: isMobile ? 0 : "24px",
      marginRight: isMobile ? 0 : "16px",
      marginTop: isMobile ? 0 : "90px",
      height: isMobile ? "100%" : "calc(100vh - 128px)",
      transition: { type: "spring", stiffness: 300, damping: 30 },
      backgroundColor: alpha(theme.palette.background.paper, 0.9),
      backdropFilter: "blur(16px) saturate(180%)", // Enhanced glass effect
      boxShadow: theme.shadows[10],
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    },
    mapHidden: {
      width: "100%",
      x: 0,
      opacity: 1,
      borderRadius: 0,
      margin: 0,
      height: "100vh",
      transition: { type: "spring", stiffness: 300, damping: 30 },
      backgroundColor: "transparent", // Let the main background show through
      backdropFilter: "none",
      boxShadow: "none",
      border: "none",
    },
  };

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        // Modern gradient background
        background: `radial-gradient(circle at 50% 0%, ${alpha(
          theme.palette.primary.light,
          0.1
        )} 0%, ${theme.palette.background.default} 60%)`,
        position: "relative",
      }}
    >
      {/* 1. Background Map Layer */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          transition: "opacity 0.5s ease",
          opacity: isMapVisible ? 1 : 0, // Fade out map when not active
          pointerEvents: isMapVisible ? "auto" : "none",
        }}
      >
        <OrdersMap sidebarOpen={isMapVisible && !isMobile} />
      </Box>

      {/* 2. Floating Top Bar "Island" */}
      {!isFullPageMode && (
        <Box
          component={motion.div}
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none", // Let clicks pass through around the bar
            p: 2,
          }}
        >
          <Paper
            elevation={4}
            sx={{
              pointerEvents: "auto", // Re-enable clicks
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 1,
              pl: 2,
              borderRadius: "100px", // Pill shape
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(16px) saturate(180%)", // Enhanced glass effect
              border: `1px solid ${alpha(theme.palette.common.white, 0.5)}`,
              maxWidth: "95%",
              boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.07)",
            }}
          >
            <DashboardTab
              alignment={alignment}
              handleAlignment={handleAlignment}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              mapViewState={mapViewState}
              onMapViewStateChange={handleMapViewStateChange}
              onShowMapPreview={handleShowMapPreview}
            />

            {/* Divider for visual separation */}
            <Box sx={{ width: "1px", height: 24, bgcolor: "divider" }} />

            <Box sx={{ width: { xs: 150, md: 300 }, transition: "width 0.3s" }}>
              <SearchBar
                searchQuery={searchQuery}
                onSearchChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleOrderSearch(e);
                }}
                onClear={handleClear}
                size="small"
                // Pass transparent styling to search bar if supported
              />
            </Box>
          </Paper>
        </Box>
      )}

      {/* 3. Main Content Layer (Sidebar / Full View) */}
      <Box
        sx={{
          position: "relative",
          zIndex: 10, // Above map, below header
          height: "100%",
          width: "100%",
          pointerEvents: "none", // Allow clicks to pass through to map
          display: "flex",
          justifyContent: "flex-end", // Align sidebar to right
        }}
      >
        <AnimatePresence mode="wait">
          {!isFullPageMode && (
            <motion.div
              key={isMapVisible ? "sidebar" : "fullscreen"}
              initial={false}
              animate={isMapVisible ? "mapVisible" : "mapHidden"}
              variants={sidebarVariants}
              style={{
                pointerEvents: "auto",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                // Background handled by variants
              }}
            >
              {/* Content Container */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  pt: isMapVisible ? 8 : 12, // More padding for header in fullscreen
                  px: isMapVisible ? 2 : 4,
                  pb: 4,
                }}
              >
                {/* Dispatch Controls (Sticky) */}
                {ordersToDispatch.length > 0 && (
                  <Box sx={{ mb: 2, position: "sticky", top: 0, zIndex: 10 }}>
                    <DispatchControls
                      ordersToDispatch={ordersToDispatch}
                      onClearSelection={clearOrdersToDispatch}
                      onDispatch={showDispatchOrdersModal}
                      orderUsage={orderUsage}
                    />
                  </Box>
                )}

                {/* List Content */}
                {switchAlignmentComponents()}

                {/* Instructions (Only in Full View) */}
                {!isMapVisible && mapViewState === "hidden" && (
                  <Box sx={{ mt: 4, maxWidth: 800, mx: "auto" }}>
                    <InstructionsPanel />
                  </Box>
                )}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Full Page Components (Saloon/Packing) */}
        {isFullPageMode && (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              pointerEvents: "auto",
              bgcolor: "background.default",
              overflowY: "auto",
              pt: 2,
            }}
          >
            {switchAlignmentComponents()}
          </Box>
        )}
      </Box>

      {/* 4. Floating Accessories */}
      <FloatingMapPreview
        show={showFloatingMap}
        onClose={handleHideMapPreview}
      />

      {shouldShowMobileToggle && (
        <Fab
          color="primary"
          aria-label="toggle-map"
          onClick={toggleMobileMapView}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 16,
            display: { md: "none" },
            zIndex: 1300,
          }}
        >
          {showMapOnMobile ? <ViewListIcon /> : <MapIcon />}
        </Fab>
      )}

      <FloatingMenu />

      {/* Plan quota reached — upgrade prompt (driven by redux) */}
      <OrderLimitModal />
    </Box>
  );
}
